import { FormikProps } from 'formik';
import { findIndex } from 'lodash';
import { DraggableLocation, DropResult } from 'react-beautiful-dnd';
import { findIndexById, moveElement } from '../../../../helpers/arrayHelpers';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { DesignFormModel } from '../../../designs/design-form/designFormModel';
import { QuoteFormModel } from '../../../quotes/quote-form/quoteFormModel';
import { manifoldFieldName, unitFieldName } from '../quoteOrDesignFormFieldNameHelpers';
import { UnitFormModel } from '../units/unitFormModel';

export const dragTypes = {
  unit: 'UNIT',
  manifold: 'MANIFOLD',
  room: 'ROOM',
};

export type ArrayFieldUpdateHandler = <TElement>(fieldName: string, value: Array<TElement>) => void;

export const handleDragEnd = (
  { source, destination, type }: DropResult,
  units: Array<UnitFormModel>,
  setFieldValue: FormikProps<QuoteFormModel | DesignFormModel>['setFieldValue'],
  setFieldTouched: FormikProps<QuoteFormModel | DesignFormModel>['setFieldTouched'],
) => {
  const updateFieldState: ArrayFieldUpdateHandler = <TElement>(fieldName: string, value: Array<TElement>) => {
    setFieldValue(fieldName, value);
    setFieldTouched(fieldName, false);
  };

  if (type === dragTypes.unit) {
    handleUnitDrag(source, destination, units, updateFieldState);
  } else if (type === dragTypes.manifold) {
    handleManifoldDrag(source, destination, units, updateFieldState);
  } else {
    handleRoomDrag(source, destination, units, updateFieldState);
  }
};

export const handleUnitDrag = (
  source: DraggableLocation,
  destination: DraggableLocation | undefined,
  units: Array<UnitFormModel>,
  updateFieldState: ArrayFieldUpdateHandler,
) => {
  if (destination == null) {
    return;
  }

  const [newUnits] = moveElement(units, units, source.index, destination.index);

  updateFieldState('units', newUnits);
};

export const handleManifoldDrag = (
  source: DraggableLocation,
  destination: DraggableLocation | undefined,
  units: Array<UnitFormModel>,
  updateFieldState: ArrayFieldUpdateHandler,
) => {
  if (destination == null) {
    return;
  }

  const dragUnitIndex = findIndexById(units, source.droppableId);
  const dropUnitIndex = findIndexById(units, destination.droppableId);

  const [newSourceManifolds, newDestinationManifolds] = moveElement(
    units[dragUnitIndex].manifolds,
    units[dropUnitIndex].manifolds,
    source.index,
    destination.index,
  );

  const isDragBetweenUnits = source.droppableId !== destination.droppableId;
  if (isDragBetweenUnits) {
    updateFieldState(unitFieldName(dragUnitIndex, 'manifolds'), newSourceManifolds);
    updateFieldState(unitFieldName(dropUnitIndex, 'manifolds'), newDestinationManifolds);
  } else {
    updateFieldState(unitFieldName(dragUnitIndex, 'manifolds'), newSourceManifolds);
  }
};

export const handleRoomDrag = (
  source: DraggableLocation,
  destination: DraggableLocation | undefined,
  units: Array<UnitFormModel>,
  updateFieldState: ArrayFieldUpdateHandler,
) => {
  if (destination == null) {
    return;
  }

  const dragUnitIndex = findIndex(units, unit => findIndexById(unit.manifolds, source.droppableId) !== -1);
  const dropUnitIndex = findIndex(units, unit => findIndexById(unit.manifolds, destination.droppableId) !== -1);
  const dragManifoldIndex = findIndex(units[dragUnitIndex].manifolds, manifold => manifold.id === source.droppableId);
  const dropManifoldIndex = findIndex(
    units[dropUnitIndex].manifolds,
    manifold => manifold.id === destination.droppableId,
  );

  const [newSourceRooms, newDestinationRooms] = moveElement(
    assertNotNull(units[dragUnitIndex].manifolds[dragManifoldIndex].rooms),
    assertNotNull(units[dropUnitIndex].manifolds[dropManifoldIndex].rooms),
    source.index,
    destination.index,
  );

  const isDragBetweenManifolds = dragUnitIndex !== dropUnitIndex || dragManifoldIndex !== dropManifoldIndex;

  if (isDragBetweenManifolds) {
    updateFieldState(manifoldFieldName(dragUnitIndex, dragManifoldIndex, 'rooms'), newSourceRooms);
    updateFieldState(manifoldFieldName(dropUnitIndex, dropManifoldIndex, 'rooms'), newDestinationRooms);
  } else {
    updateFieldState(manifoldFieldName(dragUnitIndex, dragManifoldIndex, 'rooms'), newSourceRooms);
  }
};
